import styled from '@emotion/styled';
import {
  Breadcrumb,
  Button,
  Drawer,
  Input,
  Table,
  Tag,
} from 'antd';
import moment from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import { FaArrowLeft, FaHome, FaPen, FaPlus, FaUser, FaUsers } from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { PulseLoader } from 'react-spinners';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { ProfilEntity } from '../../../../entities/profil.entity';
import { UserEntity } from '../../../../entities/utilisateur.entity';
import { ROUTES } from '../../../../routes';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { Layout } from '../../../shared/Layout';
import { ProfilForm } from '../components/ProfilForm';
import { SideBar } from '../components/SidebarItems';
import { UserForm } from '../components/UserForm';
import { getUtilisateursProfils } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background-color: white;
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  display: flex;
  color: var(--mt-text);
  font-family: Poppins-R;

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .style-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
    }
  }

  .link {
    text-decoration: underline;
    cursor: pointer;
  }

  > .content {
    flex: 1;
    min-width: 0;
    padding: 12px;
    background: transparent;
  }

  .admin-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .admin-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
  }

  .admin-hero p {
    max-width: 740px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .back-button,
  .primary-action,
  .ghost-action,
  .action-button {
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .back-button,
  .primary-action {
    border: 0;
    background: linear-gradient(135deg, #ff4f1f, ${YELLOW});
    color: #07141c;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
  }

  .action-button {
    display: inline-grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border: 1px solid var(--mt-border);
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .page-toolbar,
  .table-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card .label {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 22px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .page-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 360px) 1fr auto;
    align-items: center;
    gap: 12px;
    margin: 12px 0;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 11px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 34px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .table-shell {
    overflow: hidden;
    padding: 12px;
  }

  .ant-table {
    background: transparent;
    color: var(--mt-text);
  }

  @media (max-width: 900px) {
    flex-direction: column;

    > .content {
      padding: 18px 12px;
    }

    .admin-hero,
    .page-toolbar {
      grid-template-columns: 1fr;
      flex-direction: column;
      align-items: flex-start;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .page-toolbar .ant-space {
      flex-wrap: wrap;
    }
  }
`;

export const UserAgencePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showProfilDrawer, setShowProfilDrawer] = useState(false);
  const [showUpdateUserDrawer, setShowUpdateUserDrawer] = useState(false);
  const agence = (useLocation().state as any)?.agence;
  const [users, setUsers] = useState<UserEntity[]>(
    (useLocation().state as any)?.users,
  );
  const [userProfiles, setUserProfiles] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserEntity>();
  const [selectedUserPro, setSelectedUserPro] = useState<ProfilEntity[]>([]);
  const [filterUsers, setFilterUsers] = useState<UserEntity[]>(
    (useLocation().state as any)?.users,
  );
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const history = useHistory();

  const fetchData = () => {
    setIsLoading(true);
    getUtilisateursProfils(connectedUser.id).then(({ data }) => {
      setUserProfiles(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (agence === undefined || users === undefined) {
      history.push(ROUTES.PANEL.ADMIN.PARTENAIRE);
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setColor = (code: string) => {
    switch (code) {
      case 'admin':
        return '#f50';
      case 'resp_commercial':
        return '#3c096c';
      case 'commercial':
        return '#f9a825';
      case 'responsable':
        return '#108ee9';
      case 'chef_agence':
        return '#8d6e63';
      case 'gestionnaire':
        return '#5f0f40';
      case 'sadmin':
        return '#fb8b24';
    }
  };

  const COLUMNS = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      flex: 1,
    },
    {
      title: 'Prénom',
      dataIndex: 'prenom',
      flex: 1,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      flex: 1,
    },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      flex: 1,
    },
    {
      title: 'Contact',
      dataIndex: 'mobile',
      flex: 1,
    },
    {
      title: 'Date création',
      dataIndex: 'created_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
      flex: 1,
    },
    {
      title: 'Profils',
      render: (cell: any, row: UserEntity) => {
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {userProfiles
              .find((uPro) => row.id === uPro.id)
              ?.profils.map((pro: any) => (
                <Tag key={pro.id} color={setColor(pro.code_profil)}>
                  {pro.nom}
                </Tag>
              )) || <Tag>--</Tag>}
          </div>
        );
      },
      flex: 2,
    },
    {
      title: 'Action',
      render: (cell: any, row: UserEntity) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            className='action-button'
            icon={<FaPen />}
            onClick={() => {
              setSelectedUser(row);
              setShowUpdateUserDrawer(true);
            }}
          />
          <Button
            className='action-button'
            icon={<FaUser />}
            onClick={() => {
              setSelectedUserPro(
                userProfiles.find((uPro) => row.id === uPro.id).profils,
              );
              setSelectedUser(row);
              setShowProfilDrawer(true);
            }}
          />
        </div>
      ),
      flex: 1,
    },
  ];

  return (
    <Layout hideYellowScrollInfo>
      <Container>
        <SideBar defaultOpenKeys={['m1']} current='1.1' />{' '}
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <Breadcrumb separator='>' className='bread-crumb'>
            <Breadcrumb.Item>
              <FaHome style={{ marginRight: 5 }} />
              <span>Administration</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item
              onClick={() => history.push(ROUTES.PANEL.ADMIN.PARTENAIRE)}
            >
              <span className='link'>Partenaires</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Agences</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Utilisateurs</span>
            </Breadcrumb.Item>
          </Breadcrumb>

          <div style={{ marginTop: 20 }}>
            <section className='admin-hero'>
              <div>
                <span className='eyebrow'>Administration</span>
                <h1>Utilisateurs - {agence?.nom}</h1>
                <p>
                  Gérez les accès, les profils et les informations opérateur
                  de l’agence.
                </p>
              </div>
              <div className='hero-actions'>
                <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                  Rafraîchir
                </Button>
                <Button className='back-button' icon={<FaArrowLeft />} onClick={() => history.goBack()}>
                  Retour
                </Button>
              </div>
            </section>

            <section className='kpi-grid'>
              <KpiCard label='Utilisateurs' value={filterUsers?.length || '--'} icon={<FaUsers />} />
              <KpiCard label='Profils assignés' value={userProfiles.reduce((total, item) => total + (item.profils?.length || 0), 0) || '--'} icon={<FaUser />} />
              <KpiCard label='Agence' value={agence?.code || agence?.nom || '--'} icon={<FaHome />} />
            </section>

            <div className='page-toolbar'>
              <div className='search-box'>
                <FiSearch />
                <Input
                  placeholder='Rechercher...'
                  onChange={(e) => {
                    setFilterUsers(
                      users.filter(
                        (age) =>
                          age.nom
                            .toLowerCase()
                            .includes(e.target.value.toLowerCase()) ||
                          age.prenom
                            .toLowerCase()
                            .includes(e.target.value.toLowerCase()) ||
                          age.adresse
                            .toLowerCase()
                            .includes(e.target.value.toLowerCase()),
                      ),
                    );
                  }}
                />
              </div>
              <PulseLoader size={5} margin={2} color={YELLOW} loading={isLoading} />

              <Button
                icon={<FaPlus />}
                className='primary-action'
                onClick={() => setShowDrawer(true)}
              >
                Nouvel Utilisateur
              </Button>
            </div>

            <div className='table-shell'>
            <Table
              columns={COLUMNS}
              dataSource={filterUsers}
              rowClassName='style-row'
              rowKey='id'
              size='small'
              loading={isLoading}
              style={{ zoom: 0.9 }}
              scroll={{ x: 1000 }}
              pagination={false}
            />
            </div>
          </div>
        </div>
        <Drawer
          title='Ajouter un utilisateur'
          placement='right'
          width='min(100vw, 560px)'
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <UserForm
            agence={agence}
            setShowDrawer={setShowDrawer}
            getNewUser={(user) => {
              setUsers([...users, user]);
              setFilterUsers([...filterUsers, user]);
            }}
          />
        </Drawer>
        <Drawer
          title='Gestion des profils'
          placement='right'
          width='min(100vw, 520px)'
          onClose={() => setShowProfilDrawer(false)}
          open={showProfilDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <ProfilForm user={selectedUser} userProfils={selectedUserPro} />
        </Drawer>
        <Drawer
          title="Mettre à jour l'utilisateur"
          placement='right'
          width='min(100vw, 560px)'
          onClose={() => setShowUpdateUserDrawer(false)}
          open={showUpdateUserDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <UserForm
            userToUpdate={selectedUser}
            agence={agence}
            setShowDrawer={setShowUpdateUserDrawer}
            getNewUser={(user) => {}}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};

const KpiCard = ({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) => (
  <div className='kpi-card'>
    <div>
      <span className='label'>{label}</span>
      <strong className='value'>{value}</strong>
    </div>
    <span className='icon'>{icon}</span>
  </div>
);
